import React, { useMemo, useRef, useState } from "react";
import {
  UploadCloud,
  FileSpreadsheet,
  FileText,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Search,
  Download,
  Send,
  ChevronRight,
  ShieldCheck,
  ListChecks,
  Users,
  LayoutDashboard,
  FileBarChart,
  X,
  Loader2,
  History,
  CloudUpload,
} from "lucide-react";
import {
  useProcessDataImport,
  useNotifyImportAgents,
  useListDataImports,
  getListDataImportsQueryKey,
  DataImportKind,
  type ProcessDataImportResult,
  type ProcessedRow,
  type ImportSummary,
  type DataImport,
} from "@workspace/api-client-react";
import { useUpload } from "@workspace/object-storage-web";
import { useQueryClient } from "@tanstack/react-query";
import { formatZAR } from "@/lib/formatters";

type ImportKind = "leads" | "omoo";
type StepState = "pending" | "active" | "done";

const KIND_TO_API: Record<ImportKind, (typeof DataImportKind)[keyof typeof DataImportKind]> = {
  leads: DataImportKind.leads_report,
  omoo: DataImportKind.omoo_claim,
};

const STEPS = [
  { key: "validate", label: "Validate", icon: ShieldCheck },
  { key: "parse", label: "Parse", icon: FileBarChart },
  { key: "match", label: "Match Agents", icon: Users },
  { key: "update", label: "Update Dashboards", icon: LayoutDashboard },
  { key: "summary", label: "Generate Summary", icon: ListChecks },
] as const;

const ACCEPT = ".xlsx,.xls,.csv";

const GREEN_STATUSES = new Set(["installed", "active"]);
const RED_STATUSES = new Set(["cancelled", "rejected"]);

function prettyStatus(status: string | null | undefined): string {
  if (!status) return "—";
  return status
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function statusPill(status: string | null | undefined): string {
  const s = (status ?? "").toLowerCase();
  if (GREEN_STATUSES.has(s)) return "bg-green-100 text-[#00A859]";
  if (RED_STATUSES.has(s)) return "bg-red-100 text-red-700";
  if (s) return "bg-amber-100 text-amber-700";
  return "bg-gray-100 text-gray-500";
}

function formatDateTime(iso: string | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-ZA", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function historyBadge(row: DataImport): "Success" | "Warnings" | "Failed" {
  if (row.status === "failed") return "Failed";
  if ((row.rowsFailed ?? 0) > 0) return "Warnings";
  return "Success";
}

export function DataImport() {
  const [tab, setTab] = useState<"upload" | "history">("upload");
  const [files, setFiles] = useState<Record<ImportKind, File | null>>({
    leads: null,
    omoo: null,
  });
  const [dragKind, setDragKind] = useState<ImportKind | null>(null);
  const [processing, setProcessing] = useState<ImportKind | null>(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [result, setResult] = useState<{ kind: ImportKind; data: ProcessDataImportResult } | null>(
    null,
  );
  const [toast, setToast] = useState<{ msg: string; tone: "success" | "error" } | null>(null);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<string>("all");
  const [page, setPage] = useState(1);
  const [notifyingImportId, setNotifyingImportId] = useState<number | null>(null);
  const pageSize = 6;
  const toastTimer = useRef<number | null>(null);

  const queryClient = useQueryClient();
  const { uploadFile } = useUpload();
  const processImport = useProcessDataImport();
  const notifyAgents = useNotifyImportAgents();
  const historyQuery = useListDataImports();

  function showToast(msg: string, tone: "success" | "error" = "success") {
    setToast({ msg, tone });
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 4000);
  }

  function selectFile(kind: ImportKind, file: File | null) {
    if (!file) return;
    setFiles((f) => ({ ...f, [kind]: file }));
  }

  async function startUpload(kind: ImportKind) {
    const file = files[kind];
    if (!file) return;
    setProcessing(kind);
    setStepIndex(0);
    const timer = window.setInterval(() => {
      setStepIndex((i) => Math.min(i + 1, STEPS.length - 1));
    }, 700);
    try {
      const uploaded = await uploadFile(file);
      if (!uploaded) throw new Error("File upload failed");
      const data = await processImport.mutateAsync({
        data: {
          objectPath: uploaded.objectPath,
          fileName: file.name,
          kind: KIND_TO_API[kind],
        },
      });
      window.clearInterval(timer);
      setStepIndex(STEPS.length);
      await new Promise((r) => setTimeout(r, 450));
      setResult({ kind, data });
      setProcessing(null);
      setStepIndex(0);
      setPage(1);
      setQuery("");
      setFilter("all");
      setFiles((f) => ({ ...f, [kind]: null }));
      void queryClient.invalidateQueries({ queryKey: getListDataImportsQueryKey() });
      const s = data.summary;
      showToast(
        kind === "leads"
          ? `Leads report imported — ${s.matched ?? s.total} orders updated`
          : `OMOO statement processed — ${s.matched ?? 0} claims matched`,
      );
    } catch (err) {
      window.clearInterval(timer);
      setProcessing(null);
      setStepIndex(0);
      const msg = err instanceof Error ? err.message : "Import failed. Please try again.";
      showToast(msg, "error");
    }
  }

  const rows = result?.data.rows ?? [];
  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      const matchesQuery =
        !q ||
        [r.leadId, r.agentName, r.agentCode, r.customer, r.orderNumber, r.claimRef]
          .filter(Boolean)
          .some((v) => v!.toLowerCase().includes(q));
      const matchesFilter =
        filter === "all" ||
        (filter === "matched"
          ? r.matched
          : filter === "unmatched"
            ? !r.matched
            : (r.status ?? "").toLowerCase() === filter);
      return matchesQuery && matchesFilter;
    });
  }, [rows, query, filter]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const pagedRows = filteredRows.slice((page - 1) * pageSize, page * pageSize);

  async function handleNotify(importId: number) {
    setNotifyingImportId(importId);
    try {
      const res = await notifyAgents.mutateAsync({ id: importId });
      void queryClient.invalidateQueries({ queryKey: getListDataImportsQueryKey() });
      if (res.alreadyNotified) {
        showToast("Agents have already been notified for this import");
      } else if (res.agentsNotified === 0) {
        showToast("No order status changes to notify agents about");
      } else {
        showToast(
          `Notified ${res.agentsNotified} ${
            res.agentsNotified === 1 ? "agent" : "agents"
          } of updated order statuses`,
        );
      }
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Could not notify agents. Please try again.";
      showToast(msg, "error");
    } finally {
      setNotifyingImportId(null);
    }
  }

  function handleExport() {
    if (!result) return;
    const cols =
      result.kind === "leads"
        ? ["leadId", "agentName", "isp", "customer", "orderNumber", "status", "date"]
        : ["claimRef", "agentCode", "agentName", "isp", "product", "onceOffFeeCents", "period", "matched"];
    const header = cols.join(",");
    const lines = result.data.rows.map((r) =>
      cols
        .map((c) => {
          const v = (r as unknown as Record<string, unknown>)[c];
          const text = v == null ? "" : String(v);
          return `"${text.replace(/"/g, '""')}"`;
        })
        .join(","),
    );
    const blob = new Blob([[header, ...lines].join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${result.data.import.fileName.replace(/\.[^.]+$/, "")}_processed.csv`;
    a.click();
    URL.revokeObjectURL(url);
    showToast("Export started — CSV downloaded");
  }

  return (
    <div className="font-['Inter'] text-[#0B132B]">
      <div className="px-8 py-6 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
          <span>Operations</span>
          <ChevronRight size={15} />
          <span className="font-semibold text-[#0B132B]">Data Import</span>
        </div>
        <h1 className="text-2xl font-bold tracking-tight">Data Import — Openserve</h1>
        <p className="text-gray-500 text-sm mt-1">
          Upload Openserve reports to update order statuses and reconcile OMOO claims.
        </p>
      </div>

      <div className="p-8 space-y-8">
        <div className="flex gap-1 border-b border-gray-200">
          {[
            { key: "upload" as const, label: "Upload Reports", icon: CloudUpload },
            { key: "history" as const, label: "Upload History", icon: History },
          ].map((t) => {
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                  active
                    ? "border-[#E6007E] text-[#0B132B]"
                    : "border-transparent text-gray-500 hover:text-[#0B132B]"
                }`}
              >
                <t.icon size={17} className={active ? "text-[#E6007E]" : "text-gray-400"} />
                {t.label}
              </button>
            );
          })}
        </div>

        {tab === "upload" && (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <UploadCard
                title="Leads Report"
                subtitle="Daily / weekly order status export"
                icon={FileSpreadsheet}
                hint="Columns: Lead ID, Agent, ISP, Status, Customer, Order Number, Dates"
                file={files.leads}
                dragActive={dragKind === "leads"}
                onDragState={(active) => setDragKind(active ? "leads" : null)}
                onSelect={(f) => selectFile("leads", f)}
                onClear={() => setFiles((f) => ({ ...f, leads: null }))}
                onUpload={() => startUpload("leads")}
                busy={processing === "leads"}
              />
              <UploadCard
                title="OMOO Claim Statement"
                subtitle="Monthly once-off connection fee claims"
                icon={FileText}
                hint="Columns: Claim Ref, Agent Code, Lead ID, ISP, Product, Once-Off Fee, Period"
                file={files.omoo}
                dragActive={dragKind === "omoo"}
                onDragState={(active) => setDragKind(active ? "omoo" : null)}
                onSelect={(f) => selectFile("omoo", f)}
                onClear={() => setFiles((f) => ({ ...f, omoo: null }))}
                onUpload={() => startUpload("omoo")}
                busy={processing === "omoo"}
              />
            </div>

            {result && (
              <ResultsSection
                kind={result.kind}
                summary={result.data.summary}
                query={query}
                setQuery={(v) => {
                  setQuery(v);
                  setPage(1);
                }}
                filter={filter}
                setFilter={(v) => {
                  setFilter(v);
                  setPage(1);
                }}
                rows={pagedRows}
                totalRows={filteredRows.length}
                page={page}
                totalPages={totalPages}
                setPage={setPage}
                onExport={handleExport}
                onNotify={() => handleNotify(result.data.import.id)}
                notifying={notifyingImportId === result.data.import.id}
              />
            )}
          </>
        )}

        {tab === "history" && (
          <HistorySection
            imports={historyQuery.data?.items ?? []}
            isLoading={historyQuery.isLoading}
            onNotify={handleNotify}
            notifyingImportId={notifyingImportId}
          />
        )}
      </div>

      {processing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0B132B]/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8">
            <div className="flex flex-col items-center text-center mb-8">
              <div className="w-14 h-14 rounded-full bg-[#E6007E]/10 flex items-center justify-center mb-4">
                <Loader2 size={28} className="text-[#E6007E] animate-spin" />
              </div>
              <h3 className="text-lg font-bold">Processing import</h3>
              <p className="text-gray-500 text-sm mt-1">{files[processing]?.name}</p>
            </div>
            <div className="space-y-3">
              {STEPS.map((step, i) => {
                const s: StepState =
                  i < stepIndex ? "done" : i === stepIndex ? "active" : "pending";
                return (
                  <div key={step.key} className="flex items-center gap-3">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-colors ${
                        s === "done"
                          ? "bg-[#00A859] text-white"
                          : s === "active"
                            ? "bg-[#E6007E] text-white"
                            : "bg-gray-100 text-gray-400"
                      }`}
                    >
                      {s === "done" ? (
                        <CheckCircle2 size={16} />
                      ) : s === "active" ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <step.icon size={15} />
                      )}
                    </div>
                    <span
                      className={`text-sm font-medium ${
                        s === "pending" ? "text-gray-400" : "text-[#0B132B]"
                      }`}
                    >
                      {step.label}
                    </span>
                    {s === "done" && (
                      <CheckCircle2 size={15} className="text-[#00A859] ml-auto" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-6 right-6 z-50 animate-[diSlideIn_.3s_ease-out]">
          <div className="flex items-center gap-3 bg-white border border-gray-200 shadow-lg rounded-lg px-4 py-3 max-w-sm">
            {toast.tone === "success" ? (
              <CheckCircle2 size={20} className="text-[#00A859] shrink-0" />
            ) : (
              <XCircle size={20} className="text-red-600 shrink-0" />
            )}
            <span className="text-sm font-medium text-[#0B132B]">{toast.msg}</span>
            <button
              onClick={() => setToast(null)}
              className="ml-2 text-gray-400 hover:text-gray-600"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      <style>{`@keyframes diSlideIn{from{transform:translateX(120%);opacity:0}to{transform:translateX(0);opacity:1}}`}</style>
    </div>
  );
}

function UploadCard(props: {
  title: string;
  subtitle: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  hint: string;
  file: File | null;
  dragActive: boolean;
  busy: boolean;
  onDragState: (active: boolean) => void;
  onSelect: (file: File | null) => void;
  onClear: () => void;
  onUpload: () => void;
}) {
  const { title, subtitle, icon: Icon, hint, file, dragActive, busy } = props;
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col">
      <div className="px-6 py-5 border-b border-gray-100 flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#0B132B] text-white flex items-center justify-center shrink-0">
          <Icon size={20} />
        </div>
        <div>
          <h3 className="font-bold text-base">{title}</h3>
          <p className="text-gray-500 text-xs mt-0.5">{subtitle}</p>
        </div>
      </div>

      <div className="p-6 flex-1 flex flex-col">
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          className="hidden"
          onChange={(e) => {
            props.onSelect(e.target.files?.[0] ?? null);
            e.target.value = "";
          }}
        />
        {!file ? (
          <div
            onDragEnter={(e) => {
              e.preventDefault();
              props.onDragState(true);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              props.onDragState(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              props.onDragState(false);
            }}
            onDrop={(e) => {
              e.preventDefault();
              props.onDragState(false);
              props.onSelect(e.dataTransfer.files?.[0] ?? null);
            }}
            className={`border-2 border-dashed rounded-xl p-8 text-center transition-all flex-1 flex flex-col items-center justify-center ${
              dragActive
                ? "border-[#E6007E] bg-pink-50/40"
                : "border-gray-200 bg-gray-50/60 hover:border-gray-300"
            }`}
          >
            <div className="w-14 h-14 bg-white rounded-full shadow-sm flex items-center justify-center mb-3 text-[#E6007E]">
              <UploadCloud size={28} />
            </div>
            <p className="font-semibold text-sm">Drag &amp; drop your file here</p>
            <p className="text-gray-500 text-xs mt-1 mb-4">
              or click to browse — .xlsx, .xls, .csv (max 10MB)
            </p>
            <button
              onClick={() => inputRef.current?.click()}
              className="px-5 py-2 bg-[#0B132B] text-white rounded-md font-medium text-sm hover:bg-opacity-90 transition-colors"
            >
              Select File
            </button>
          </div>
        ) : (
          <div className="flex-1 flex flex-col">
            <div className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
              <FileSpreadsheet size={20} className="text-[#E6007E] shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{file.name}</p>
                <p className="text-xs text-gray-500">Ready to upload</p>
              </div>
              <button
                onClick={props.onClear}
                className="text-gray-400 hover:text-red-600 shrink-0"
              >
                <X size={18} />
              </button>
            </div>
          </div>
        )}

        <p className="text-xs text-gray-400 mt-4 leading-relaxed">{hint}</p>

        <button
          disabled={!file || busy}
          onClick={props.onUpload}
          className={`mt-4 w-full py-2.5 rounded-md font-semibold text-sm flex items-center justify-center gap-2 transition-all ${
            file && !busy
              ? "bg-[#E6007E] text-white hover:bg-opacity-90 shadow-sm"
              : "bg-gray-100 text-gray-400 cursor-not-allowed"
          }`}
        >
          <UploadCloud size={16} /> Upload &amp; Process
        </button>
      </div>
    </div>
  );
}

function ResultsSection(props: {
  kind: ImportKind;
  summary: ImportSummary;
  query: string;
  setQuery: (v: string) => void;
  filter: string;
  setFilter: (v: string) => void;
  rows: ProcessedRow[];
  totalRows: number;
  page: number;
  totalPages: number;
  setPage: (n: number) => void;
  onExport: () => void;
  onNotify: () => void;
  notifying: boolean;
}) {
  const { kind, summary } = props;
  const isLeads = kind === "leads";

  const stats: ReadonlyArray<{ label: string; value: string; tone: string }> = isLeads
    ? [
        { label: "Total Records", value: summary.total.toLocaleString("en-ZA"), tone: "navy" },
        { label: "Created", value: (summary.created ?? 0).toLocaleString("en-ZA"), tone: "green" },
        { label: "Updated", value: (summary.updated ?? 0).toLocaleString("en-ZA"), tone: "amber" },
        {
          label: "Unchanged",
          value: (summary.unchanged ?? 0).toLocaleString("en-ZA"),
          tone: "navy",
        },
        { label: "Installed", value: summary.installed.toLocaleString("en-ZA"), tone: "green" },
        { label: "Pending", value: summary.pending.toLocaleString("en-ZA"), tone: "amber" },
        { label: "Cancelled", value: summary.cancelled.toLocaleString("en-ZA"), tone: "red" },
        {
          label: "Unmatched Agents",
          value: (summary.agentsUnmatched ?? 0).toLocaleString("en-ZA"),
          tone: "amber",
        },
      ]
    : [
        { label: "Total Claims", value: summary.total.toLocaleString("en-ZA"), tone: "navy" },
        {
          label: "Matched",
          value: (summary.matched ?? 0).toLocaleString("en-ZA"),
          tone: "green",
        },
        {
          label: "Credited",
          value: (summary.credited ?? 0).toLocaleString("en-ZA"),
          tone: "green",
        },
        {
          label: "Already Credited",
          value: (summary.alreadyCredited ?? 0).toLocaleString("en-ZA"),
          tone: "navy",
        },
        {
          label: "Commission Paid",
          value: formatZAR(summary.creditedValueCents ?? 0),
          tone: "magenta",
        },
        {
          label: "Retainers",
          value: formatZAR(summary.retainerValueCents ?? 0),
          tone: "navy",
        },
        {
          label: "Unmatched",
          value: (summary.unmatched ?? 0).toLocaleString("en-ZA"),
          tone: "amber",
        },
        {
          label: "Flagged Rates",
          value: (summary.flaggedUnmapped ?? 0).toLocaleString("en-ZA"),
          tone: "red",
        },
      ];

  const toneMap: Record<string, string> = {
    navy: "text-[#0B132B]",
    green: "text-[#00A859]",
    amber: "text-amber-600",
    red: "text-red-600",
    magenta: "text-[#E6007E]",
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 bg-green-50 border border-green-100 rounded-lg px-5 py-4">
        <CheckCircle2 size={22} className="text-[#00A859]" />
        <div>
          <p className="font-semibold text-sm">Import completed successfully</p>
          <p className="text-xs text-gray-600">
            {isLeads
              ? `${summary.total.toLocaleString("en-ZA")} records processed, ${(summary.matched ?? 0).toLocaleString("en-ZA")} orders matched across ${summary.agentsUpdated} agents. Dashboards updated.`
              : `${summary.total.toLocaleString("en-ZA")} OMOO claims parsed, ${(summary.matched ?? 0).toLocaleString("en-ZA")} matched to leads. Ready for finance review.`}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map((s) => (
          <div key={s.label} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <p className="text-xs text-gray-500 font-medium">{s.label}</p>
            <p className={`text-2xl font-bold mt-1 ${toneMap[s.tone]}`}>{s.value}</p>
          </div>
        ))}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b border-gray-100">
          <div className="flex items-center gap-3 flex-1">
            <div className="relative flex-1 max-w-xs">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                value={props.query}
                onChange={(e) => props.setQuery(e.target.value)}
                placeholder={
                  isLeads
                    ? "Search lead, agent, customer, order…"
                    : "Search claim, agent code, lead…"
                }
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#E6007E]"
              />
            </div>
            <select
              value={props.filter}
              onChange={(e) => props.setFilter(e.target.value)}
              className="text-sm border border-gray-200 rounded-md py-2 px-3 focus:outline-none focus:border-[#E6007E]"
            >
              {isLeads ? (
                <>
                  <option value="all">All statuses</option>
                  <option value="installed">Installed</option>
                  <option value="active">Active</option>
                  <option value="pending">Pending</option>
                  <option value="in_progress">In Progress</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="rejected">Rejected</option>
                  <option value="unmatched">Unmatched agent</option>
                </>
              ) : (
                <>
                  <option value="all">All claims</option>
                  <option value="matched">Matched only</option>
                </>
              )}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={props.onExport}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
            >
              <Download size={15} /> Export
            </button>
            <button
              onClick={props.onNotify}
              disabled={props.notifying}
              className="flex items-center gap-2 px-3 py-2 text-sm font-semibold bg-[#E6007E] text-white rounded-md hover:bg-opacity-90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {props.notifying ? (
                <Loader2 size={15} className="animate-spin" />
              ) : (
                <Send size={15} />
              )}
              {props.notifying ? "Notifying…" : "Notify Agents"}
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-gray-50 text-gray-500 font-medium border-b border-gray-200">
              {isLeads ? (
                <tr>
                  <th className="px-4 py-3">Lead ID</th>
                  <th className="px-4 py-3">Agent</th>
                  <th className="px-4 py-3">ISP</th>
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">Order Number</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Date</th>
                </tr>
              ) : (
                <tr>
                  <th className="px-4 py-3">Claim Ref</th>
                  <th className="px-4 py-3">Agent Code</th>
                  <th className="px-4 py-3">Agent</th>
                  <th className="px-4 py-3">Product</th>
                  <th className="px-4 py-3">Once-Off Fee</th>
                  <th className="px-4 py-3">Commission</th>
                  <th className="px-4 py-3">Period</th>
                  <th className="px-4 py-3">Match</th>
                </tr>
              )}
            </thead>
            <tbody className="divide-y divide-gray-200">
              {props.rows.map((r, idx) =>
                isLeads ? (
                  <tr key={idx} className="hover:bg-gray-50/50">
                    <td className="px-4 py-3 font-mono text-xs">{r.leadId ?? "—"}</td>
                    <td className="px-4 py-3 font-medium">
                      <span className="inline-flex items-center gap-1.5">
                        {r.agentName ?? "—"}
                        {!r.matched ? (
                          <span
                            title={r.note ?? "Agent not recognised — assign manually"}
                            className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700"
                          >
                            Unmatched
                          </span>
                        ) : null}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{r.isp ?? "—"}</td>
                    <td className="px-4 py-3 text-gray-600">{r.customer ?? "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs">{r.orderNumber ?? "—"}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusPill(r.status)}`}
                      >
                        {prettyStatus(r.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500">{r.date ?? "—"}</td>
                  </tr>
                ) : (
                  <tr key={idx} className="hover:bg-gray-50/50">
                    <td className="px-4 py-3 font-mono text-xs">{r.claimRef ?? "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs">{r.agentCode ?? "—"}</td>
                    <td className="px-4 py-3 font-medium">{r.agentName ?? "—"}</td>
                    <td className="px-4 py-3 text-gray-600">{r.product ?? "—"}</td>
                    <td className="px-4 py-3 tabular-nums">
                      {r.onceOffFeeCents != null ? formatZAR(r.onceOffFeeCents) : "—"}
                    </td>
                    <td className="px-4 py-3 tabular-nums">
                      <span className="inline-flex items-center gap-1.5">
                        {r.commissionCents != null ? formatZAR(r.commissionCents) : "—"}
                        {r.rateFlagged ? (
                          <span
                            title="Rate inferred from speed ladder — review"
                            className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-700"
                          >
                            Flagged
                          </span>
                        ) : null}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500">{r.period ?? "—"}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          r.matched ? "bg-green-100 text-[#00A859]" : "bg-amber-100 text-amber-700"
                        }`}
                      >
                        {r.matched ? "Matched" : "Unmatched"}
                      </span>
                    </td>
                  </tr>
                ),
              )}
              {props.rows.length === 0 && (
                <tr>
                  <td colSpan={isLeads ? 7 : 8} className="px-4 py-10 text-center text-gray-400 text-sm">
                    No records match your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
          <span className="text-xs text-gray-500">
            Showing {props.rows.length} of {props.totalRows} records
          </span>
          <div className="flex items-center gap-2">
            <button
              disabled={props.page <= 1}
              onClick={() => props.setPage(props.page - 1)}
              className="px-3 py-1 text-sm border border-gray-200 rounded-md disabled:opacity-40 hover:bg-white"
            >
              Prev
            </button>
            <span className="text-xs text-gray-500">
              Page {props.page} of {props.totalPages}
            </span>
            <button
              disabled={props.page >= props.totalPages}
              onClick={() => props.setPage(props.page + 1)}
              className="px-3 py-1 text-sm border border-gray-200 rounded-md disabled:opacity-40 hover:bg-white"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function HistorySection(props: {
  imports: DataImport[];
  isLoading: boolean;
  onNotify: (importId: number) => void;
  notifyingImportId: number | null;
}) {
  const sorted = useMemo(
    () =>
      [...props.imports].sort((a, b) => {
        const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return tb - ta;
      }),
    [props.imports],
  );

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100">
        <h2 className="font-bold text-base">Upload History</h2>
        <p className="text-xs text-gray-500 mt-0.5">
          Chronological record of all Openserve imports
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead className="bg-gray-50 text-gray-500 font-medium border-b border-gray-200">
            <tr>
              <th className="px-6 py-3">File Name</th>
              <th className="px-6 py-3">Type</th>
              <th className="px-6 py-3">Uploaded By</th>
              <th className="px-6 py-3">Date</th>
              <th className="px-6 py-3">Records</th>
              <th className="px-6 py-3">Status</th>
              <th className="px-6 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {props.isLoading && (
              <tr>
                <td colSpan={7} className="px-6 py-10 text-center text-gray-400 text-sm">
                  <Loader2 size={18} className="inline animate-spin mr-2" />
                  Loading history…
                </td>
              </tr>
            )}
            {!props.isLoading && sorted.length === 0 && (
              <tr>
                <td colSpan={7} className="px-6 py-10 text-center text-gray-400 text-sm">
                  No imports recorded yet.
                </td>
              </tr>
            )}
            {sorted.map((h) => {
              const isLeads = h.source === "leads_report";
              const badge = historyBadge(h);
              const canNotify = isLeads && h.status !== "failed";
              const isNotifying = props.notifyingImportId === h.id;
              const alreadyNotified = h.notifiedAt != null;
              return (
                <tr key={h.id} className="hover:bg-gray-50/50">
                  <td className="px-6 py-4 font-medium">
                    <span className="flex items-center gap-2">
                      {isLeads ? (
                        <FileSpreadsheet size={16} className="text-[#E6007E]" />
                      ) : (
                        <FileText size={16} className="text-[#0B132B]" />
                      )}
                      {h.fileName}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-gray-600">
                    {isLeads ? "Leads Report" : "OMOO Statement"}
                  </td>
                  <td className="px-6 py-4 text-gray-600">{h.importedByName ?? "—"}</td>
                  <td className="px-6 py-4 text-gray-500">{formatDateTime(h.createdAt)}</td>
                  <td className="px-6 py-4 tabular-nums">
                    {(h.rowsTotal ?? 0).toLocaleString("en-ZA")}
                  </td>
                  <td className="px-6 py-4">
                    <HistoryBadge status={badge} />
                  </td>
                  <td className="px-6 py-4">
                    {!canNotify ? (
                      <span className="text-gray-400">—</span>
                    ) : alreadyNotified ? (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                        <CheckCircle2 size={12} /> Notified
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => props.onNotify(h.id)}
                        disabled={isNotifying}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-[#E6007E] text-white rounded-md hover:bg-opacity-90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {isNotifying ? (
                          <Loader2 size={13} className="animate-spin" />
                        ) : (
                          <Send size={13} />
                        )}
                        {isNotifying ? "Notifying…" : "Notify Agents"}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function HistoryBadge({ status }: { status: "Success" | "Warnings" | "Failed" }) {
  if (status === "Success")
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-[#00A859]">
        <CheckCircle2 size={12} /> Success
      </span>
    );
  if (status === "Warnings")
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
        <AlertTriangle size={12} /> Warnings
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
      <XCircle size={12} /> Failed
    </span>
  );
}
